import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, Download, FileSpreadsheet, Archive, ArchiveRestore, Paperclip, Eye, X, Filter } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "./PageHeader";
import { EmptyState } from "./EmptyState";

export default function ResourceManager({
  title, subtitle, resource, columns, fields, exportCollection, lookupsNeeded = [], defaultForm = {}, archivable = false, attach = null,
  urlFilters = {}, validate = null,
}) {
  const [params, setParams] = useSearchParams();
  const activeUrlFilters = Object.keys(urlFilters).filter((k) => params.get(k));
  const clearUrlFilter = (k) => { const p = new URLSearchParams(params); p.delete(k); setParams(p); };
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [lookups, setLookups] = useState({});
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState([]);
  const [attachDocs, setAttachDocs] = useState({});
  const [attachRow, setAttachRow] = useState(null);
  const [uploadingAttach, setUploadingAttach] = useState(false);
  const [viewer, setViewer] = useState(null);
  const fileRef = useRef(null);

  const load = () => {
    api.get(`/${resource}${archivable ? `?archived=${showArchived}` : ""}`).then((r) => { setRows(r.data); setSelected([]); }).catch((e) => toast.error(apiError(e)));
  };

  const loadAttachments = () => {
    if (!attach) return;
    api.get(`/documents?ref_type=${attach.refType}`).then((r) => {
      const byRef = {};
      r.data.forEach((d) => { if (d.ref_id) (byRef[d.ref_id] = byRef[d.ref_id] || []).push(d); });
      // also surface documents linked by vehicle + category
      api.get(`/documents`).then((all) => {
        const map = { ...byRef };
        (all.data || []).forEach((d) => {
          if (attach.matchVehicle && attach.category && d.category === attach.category && d.vehicle_id && !d.ref_id) {
            (rows || []).forEach((row) => {
              if (row.vehicle_id === d.vehicle_id) {
                map[row.id] = map[row.id] || [];
                if (!map[row.id].some((x) => x.id === d.id)) map[row.id].push(d);
              }
            });
          }
        });
        setAttachDocs(map);
      }).catch(() => setAttachDocs(byRef));
    }).catch(() => {});
  };


  useEffect(() => {
    load();
    lookupsNeeded.forEach((l) => {
      api.get(`/${l}`).then((r) => setLookups((prev) => ({ ...prev, [l]: r.data }))).catch(() => {});
    });
    // eslint-disable-next-line
  }, [resource, showArchived]);

  useEffect(() => { if (attach && rows) loadAttachments(); /* eslint-disable-next-line */ }, [rows]);

  const openAttachPicker = (row) => { setAttachRow(row); fileRef.current?.click(); };
  const onAttachFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !attachRow) return;
    setUploadingAttach(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("category", attach.category || "Autre");
    fd.append("ref_type", attach.refType);
    fd.append("ref_id", attachRow.id);
    if (attachRow.vehicle_id) fd.append("vehicle_id", attachRow.vehicle_id);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document joint");
      loadAttachments();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploadingAttach(false); setAttachRow(null); if (fileRef.current) fileRef.current.value = ""; }
  };
  const openViewer = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: d.content_type || res.data.type || "application/pdf" });
      setViewer({ id: d.id, name: d.original_filename, url: URL.createObjectURL(blob) });
    } catch (e) { toast.error(apiError(e)); }
  };
  const closeViewer = () => { setViewer((v) => { if (v?.url) URL.revokeObjectURL(v.url); return null; }); };
  const downloadDoc = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.original_filename || "document"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteDoc = async (d) => {
    try { await api.delete(`/documents/${d.id}`); toast.success("Document supprimé"); loadAttachments(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const archive = async (row, val) => {
    try {
      await api.post(`/${resource}/${row.id}/archive`, { archived: val });
      toast.success(val ? "Archivé" : "Restauré");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const bulkArchive = async () => {
    try {
      await Promise.all(selected.map((id) => api.post(`/${resource}/${id}/archive`, { archived: !showArchived })));
      toast.success(`${selected.length} élément(s) ${showArchived ? "restauré(s)" : "archivé(s)"}`);
      setSelected([]); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const optionsFor = (field) => {
    if (field.type === "vehicle") {
      return (lookups.vehicles || []).map((v) => ({ value: v.id, label: `${v.brand} ${v.model} (${v.registration})` }));
    }
    if (field.type === "employee") {
      return (lookups.employees || []).map((e) => ({ value: e.id, label: `${e.first_name} ${e.last_name}` }));
    }
    return (field.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  };

  const openCreate = () => { setEditing(null); setForm(defaultForm); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setOpen(true); };

  const submit = async () => {
    const err = validate && validate(form);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/${resource}/${editing.id}`, form);
        toast.success("Modifié avec succès");
      } else {
        await api.post(`/${resource}`, form);
        toast.success("Créé avec succès");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    try {
      await api.delete(`/${resource}/${row.id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doExport = async (fmt) => {
    try {
      const res = await api.get(`/export/${exportCollection}?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${exportCollection}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = useMemo(() => {
    if (!rows) return [];
    let out = rows.filter((r) => activeUrlFilters.every((k) => urlFilters[k].test(r, params.get(k))));
    if (!search) return out;
    const q = search.toLowerCase();
    return out.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
    // eslint-disable-next-line
  }, [rows, search, params]);

  return (
    <div>
      {attach && <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.gif" className="hidden" onChange={onAttachFile} data-testid={`${resource}-attach-input`} />}
      {viewer && (
        <Dialog open onOpenChange={(o) => { if (!o) closeViewer(); }}>
          <DialogContent className="max-h-[95vh] w-[96vw] max-w-5xl">
            <DialogHeader><DialogTitle className="truncate pr-8 font-heading" data-testid="doc-viewer-title">{viewer.name}</DialogTitle></DialogHeader>
            <iframe title={viewer.name} src={viewer.url} className="h-[76vh] w-full rounded-lg border border-[#E2E8F0] bg-[#F5F7FA]" data-testid="doc-viewer-frame" />
            <DialogFooter>
              <Button variant="outline" onClick={() => downloadDoc({ id: viewer.id, original_filename: viewer.name })} data-testid="doc-viewer-download"><Download className="mr-2 h-4 w-4" />Télécharger</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <PageHeader title={title} subtitle={subtitle}>
        {exportCollection && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid={`${resource}-export-btn`}><Download className="mr-2 h-4 w-4" />Exporter</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => doExport("xlsx")}><FileSpreadsheet className="mr-2 h-4 w-4" />Excel (.xlsx)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => doExport("csv")}><FileSpreadsheet className="mr-2 h-4 w-4" />CSV</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Button onClick={openCreate} data-testid={`${resource}-add-btn`} className="bg-[#A5894E] hover:bg-[#8F7440]">
          <Plus className="mr-2 h-4 w-4" />Ajouter
        </Button>
      </PageHeader>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8695AB]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" className="bg-white pl-9" data-testid={`${resource}-search`} />
        </div>
        {archivable && (
          <div className="flex rounded-lg border border-[#E2E8F0] bg-white p-0.5">
            <button onClick={() => setShowArchived(false)} className={`rounded px-3 py-1.5 text-sm font-medium ${!showArchived ? "bg-[#001A3B] text-white" : "text-[#4A5568]"}`} data-testid={`${resource}-tab-active`}>Actifs</button>
            <button onClick={() => setShowArchived(true)} className={`rounded px-3 py-1.5 text-sm font-medium ${showArchived ? "bg-[#001A3B] text-white" : "text-[#4A5568]"}`} data-testid={`${resource}-tab-archived`}>Archivés</button>
          </div>
        )}
        {activeUrlFilters.map((k) => (
          <span key={k} className="inline-flex items-center gap-1.5 rounded-full border border-[#A5894E]/40 bg-[#F4EFE3] px-3 py-1 text-xs font-medium text-[#6b5626]" data-testid={`url-filter-${k}`}>
            <Filter className="h-3 w-3" />{urlFilters[k].label(params.get(k))}
            <button onClick={() => clearUrlFilter(k)} className="rounded-full p-0.5 hover:bg-[#A5894E]/20" title="Retirer le filtre" data-testid={`url-filter-clear-${k}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
        {rows && <span className="text-sm text-[#8695AB]">{filtered.length} élément(s)</span>}
      </div>

      {archivable && selected.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-[#001A3B]/20 bg-[#EEF2F8] px-4 py-2.5" data-testid={`${resource}-bulk-bar`}>
          <span className="text-sm font-medium text-[#001A3B]">{selected.length} sélectionné(s)</span>
          <Button size="sm" onClick={bulkArchive} className="bg-[#A5894E] hover:bg-[#8F7440]" data-testid={`${resource}-bulk-archive`}>
            {showArchived ? <><ArchiveRestore className="mr-2 h-4 w-4" />Restaurer la sélection</> : <><Archive className="mr-2 h-4 w-4" />Archiver la sélection</>}
          </Button>
          <button onClick={() => setSelected([])} className="text-sm text-[#4A5568] hover:underline">Tout désélectionner</button>
        </div>
      )}
      {rows === null ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState title="Aucun élément" description="Ajoutez votre premier élément pour commencer." action={<Button onClick={openCreate} className="bg-[#A5894E] hover:bg-[#8F7440]"><Plus className="mr-2 h-4 w-4" />Ajouter</Button>} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E2E8F0] bg-[#F5F7FA] text-left text-xs uppercase tracking-wide text-[#4A5568]">
                  {archivable && <th className="px-4 py-3"><input type="checkbox" aria-label="Tout sélectionner" checked={filtered.length > 0 && selected.length === filtered.length} onChange={() => setSelected(selected.length === filtered.length ? [] : filtered.map((r) => r.id))} data-testid={`${resource}-select-all`} /></th>}
                  {columns.map((c) => <th key={c.key} className="px-4 py-3 font-semibold">{c.label}</th>)}
                  <th className="px-4 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.id} className="border-b border-[#EDF0F5] transition-colors last:border-0 hover:bg-[#F5F7FA]/60" data-testid={`${resource}-row`}>
                    {archivable && <td className="px-4 py-3"><input type="checkbox" checked={selected.includes(row.id)} onChange={() => toggleSelect(row.id)} data-testid={`${resource}-select`} /></td>}
                    {columns.map((c) => (
                      <td key={c.key} className="px-4 py-3 text-[#18221F]">
                        {c.render ? c.render(row, lookups) : (row[c.key] ?? "—")}
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {attach && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="relative rounded-lg p-1.5 text-[#4A5568] hover:bg-[#EEF2F8] hover:text-[#001A3B]" title="Documents" data-testid={`${resource}-attach-btn`}>
                                <Paperclip className="h-4 w-4" />
                                {(attachDocs[row.id]?.length || 0) > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#A5894E] px-1 text-[10px] font-semibold text-white">{attachDocs[row.id].length}</span>}
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-64">
                              {(attachDocs[row.id] || []).map((d) => (
                                <div key={d.id} className="flex items-center gap-1 px-1 py-0.5">
                                  <button onClick={() => openViewer(d)} className="flex min-w-0 flex-1 items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-[#EEF2F8]" data-testid={`${resource}-doc-view`} title="Lire">
                                    <Eye className="h-3.5 w-3.5 shrink-0 text-[#001A3B]" /><span className="truncate">{d.original_filename}</span>
                                  </button>
                                  <button onClick={() => downloadDoc(d)} className="rounded p-1.5 text-[#4A5568] hover:bg-[#EEF2F8] hover:text-[#001A3B]" title="Télécharger" data-testid={`${resource}-doc-download`}><Download className="h-3.5 w-3.5" /></button>
                                  <button onClick={() => deleteDoc(d)} className="rounded p-1.5 text-[#4A5568] hover:bg-red-50 hover:text-red-600" data-testid={`${resource}-doc-delete`} title="Supprimer"><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                              ))}
                              {(attachDocs[row.id]?.length || 0) === 0 && <div className="px-3 py-2 text-xs text-[#8695AB]">Aucun document</div>}
                              <DropdownMenuItem onClick={() => openAttachPicker(row)} disabled={uploadingAttach} data-testid={`${resource}-doc-upload`} className="mt-1 border-t font-medium text-[#001A3B]"><Paperclip className="mr-2 h-4 w-4" />{uploadingAttach ? "Envoi…" : (attach.label || "Ajouter un PDF")}</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        <button onClick={() => openEdit(row)} className="rounded-lg p-1.5 text-[#4A5568] hover:bg-[#EEF2F8] hover:text-[#001A3B]" title="Modifier" data-testid={`${resource}-edit-btn`}><Pencil className="h-4 w-4" /></button>
                        {archivable && (showArchived
                          ? <button onClick={() => archive(row, false)} className="rounded-lg p-1.5 text-[#4A5568] hover:bg-emerald-50 hover:text-emerald-700" title="Restaurer" data-testid={`${resource}-restore-btn`}><ArchiveRestore className="h-4 w-4" /></button>
                          : <button onClick={() => archive(row, true)} className="rounded-lg p-1.5 text-[#4A5568] hover:bg-amber-50 hover:text-amber-700" title="Archiver" data-testid={`${resource}-archive-btn`}><Archive className="h-4 w-4" /></button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="rounded-lg p-1.5 text-[#4A5568] hover:bg-red-50 hover:text-red-600" data-testid={`${resource}-delete-btn`}><Trash2 className="h-4 w-4" /></button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                              <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(row)} className="bg-red-600 hover:bg-red-700" data-testid={`${resource}-confirm-delete`}>Supprimer</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifier" : "Ajouter"} — {title}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.key} className={f.full || f.type === "multi" || f.type === "section" ? "sm:col-span-2" : ""}>
                {f.type === "section" ? (
                  <div className="mt-2 border-t border-[#E2E8F0] pt-4 font-heading text-sm font-semibold text-[#001A3B]">{f.label}</div>
                ) : <Label className="mb-1.5 block text-xs font-medium text-[#4A5568]">{f.label}</Label>}
                {f.type === "section" ? null : f.type === "multi" ? (
                  <div className="flex flex-wrap gap-1.5" data-testid={`field-${f.key}`}>
                    {f.options.map((o) => {
                      const cur = Array.isArray(form[f.key]) ? form[f.key] : [];
                      const on = cur.includes(o);
                      return <button type="button" key={o} onClick={() => setForm({ ...form, [f.key]: on ? cur.filter((x) => x !== o) : [...cur, o] })} className={`min-w-[2.75rem] rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors ${on ? "border-[#001A3B] bg-[#001A3B] text-white" : "border-[#E2E8F0] bg-white text-[#4A5568] hover:border-[#001A3B]/40"}`} data-testid={`field-${f.key}-${o}`}>{o}</button>;
                    })}
                  </div>
                ) : f.type === "textarea" ? (
                  <Textarea value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`field-${f.key}`} />
                ) : f.type === "select" || f.type === "vehicle" || f.type === "employee" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                    <SelectContent>
                      {optionsFor(f).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })}
                    data-testid={`field-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#A5894E] hover:bg-[#8F7440]" data-testid={`${resource}-save-btn`}>
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
